import React, { useState, useEffect } from 'react';

// material-ui
import { Chip, Grid, Paper, MenuItem, Stack, styled, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, FormControl, InputLabel, Select, SelectChangeEvent, OutlinedInput, InputAdornment} from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';

//components
import AlertInfo from '../../../dataObjects/AlertInfo';
import AlertInfoDialog from '../../../components/elements/AlertInfoDialog';
import DatabaseObject from '../dataObjects/DatabaseObject';
import DatabasePermission from '../dataObjects/DatabasePermission';
import { Search } from '@mui/icons-material';

interface Database {
    name: string;
    value: string;
}

const SqlDBs = [
    {
        name: "Archer",
        value: "archer3"
    },
    {
        name: "Audit",
        value: "audit"
    },
    {
        name: "Bank Reporter",
        value: "bankreporter"
    },
    {
        name: "Cloud",
        value: "cloud_shared"
    },
    {
        name: "E-Reports",
        value: "ereports"
    },
    {
        name: "Extranet",
        value: "extranet"
    },
    {
        name: "Filers",
        value: "filers"
    },
    {
        name: "Filing Notices",
        value: "filingnotices"
    },
    {
        name: "Legal",
        value: "Legal"
    },
    {
        name: "Public_API",
        value: "public_api"
    },
    {
        name: "Reporter 7",
        value: "reporter7"
    },
    {
        name: "Reporter7_AUX",
        value: "reporter"
    },
    {
        name: "Users",
        value: "users"
    },
]

const StyledTableRow = styled(TableRow)(() => ({
    '&:nth-of-type(odd)': {
        backgroundColor: '#fafafa',
    },
    // hide last border
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const SqlServerStatus = (): JSX.Element => {

    //state initial setup
    const [currentDBName, setDbName] = useState('filers');
    const [dbObjects, setDbObjects] = useState<DatabaseObject[]>();
    const [allDbObjects, setAllDbObjects] = useState<DatabaseObject[]>();

    //payload
    useEffect(() => {
        getDbObjects();
    }, [currentDBName]);


    /*Error Handling*/
    const [isAlertInfoDialogOpen, setAlertInfoDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState<AlertInfo>();
    const closeAlertInfoDialog = () => {
        setAlertInfoDialog(false);
    }


    /*Methods*/

    const displayStatus = (permission: DatabasePermission) => {
        let right = permission.PermissionType;
        switch (right) {
            case "SELECT":
                return (<Chip label={`${permission.DatabaseUserName} - ${permission.PermissionType}`} color="success" variant="outlined"></Chip>);
            case "INSERT":
                return (<Chip label={`${permission.DatabaseUserName} - ${permission.PermissionType}`} color="primary" variant="outlined"></Chip>);
            default:
                return ("");
        }
    }

    const displaySelectPermissions = (permissions: DatabasePermission[]) => {

        let selectedPermissions = permissions.filter(permission => permission.PermissionType === 'SELECT')
        if (selectedPermissions.length > 0) {
            return (
                <Stack
                    direction="row"
                    sx={{
                        flexWrap: 'wrap',
                        rowGap: 1,
                        columnGap: 1
                    }}>
                    {
                        selectedPermissions.map((permission: DatabasePermission, index) =>
                            <Chip sx={{ width: 'fit-content'}} key={index} label={`${permission.DatabaseUserName}`} color="success" variant="outlined"></Chip>
                        )
                    }
                </Stack>
            )
        }
        else {
            return ("");
        }
    }

    const displayInsertPermissions = (permissions: DatabasePermission[]) => {

        let selectedPermissions = permissions.filter(permission => permission.PermissionType === 'INSERT')
        if (selectedPermissions.length > 0) {
            return (
                <Stack
                    direction="row"
                    sx={{
                        flexWrap: 'wrap',
                        rowGap: 1,
                        columnGap: 1
                    }}
                >
                    {
                        selectedPermissions.map((permission: DatabasePermission, index) =>
                            <Chip sx={{ width: 'fit-content' }} key={index} label={`${permission.DatabaseUserName}`} color="primary" variant="outlined"></Chip>
                        )
                    }
                </Stack>
            )
        }
        else {
            return ("");
        }
    }

    const displayUpdatePermissions = (permissions: DatabasePermission[]) => {

        let selectedPermissions = permissions.filter(permission => permission.PermissionType === 'UPDATE')
        if (selectedPermissions.length > 0) {
            return (
                <Stack direction="row"
                    sx={{
                        flexWrap: 'wrap',
                        rowGap: 1,
                        columnGap: 1
                    }}
                >
                    {
                        selectedPermissions.map((permission: DatabasePermission, index) =>
                            <Chip sx={{ width: 'fit-content' }} key={index} label={`${permission.DatabaseUserName}`} color="warning" variant="outlined"></Chip>
                        )
                    }
                </Stack>
            )
        }
        else {
            return ("");
        }
    }

    const displayDeletePermissions = (permissions: DatabasePermission[]) => {

        let selectedPermissions = permissions.filter(permission => permission.PermissionType === 'DELETE')
        if (selectedPermissions.length > 0) {
            return (
                <Stack direction="row"
                    sx={{
                        flexWrap: 'wrap',
                        rowGap: 1,
                        columnGap: 1
                    }}
                >
                    {
                        selectedPermissions.map((permission: DatabasePermission, index) =>
                            <Chip sx={{ width: 'fit-content' }} key={index} label={`${permission.DatabaseUserName}`} color="error" variant="outlined"></Chip>
                        )
                    }
                </Stack>
            )
        }
        else {
            return ("");
        }
    }
    

    const switchDatabase = (event: SelectChangeEvent<string>) => {
        let dbName = event.target.value;
        setDbName(dbName);
    };

    const getDbObjects = async () => {
        await fetch(`/SqlDashboard/GetObjects?dbName=${currentDBName}`)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: SqlDashboard - GetObjects',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    setDbObjects(result);
                    setAllDbObjects(result);
                }
            }
        )
    }

    const updateSearchInput = (event: React.ChangeEvent<HTMLInputElement>) => {

        let searchPhrase = event.target.value;
        setTimeout(() => {
            if (!searchPhrase) {
                setDbObjects(allDbObjects);
            }
            else {
                let filteredObjects = allDbObjects?.filter(mr => mr.Name.toLowerCase().includes(searchPhrase.toLowerCase()));
                setDbObjects(filteredObjects);
            }
        }, 1000)
    }


    return (
        <div>
            <Grid container sx={{ display: 'flex' }} justifyContent="center">
                <Grid item xs={11} marginTop={3}>
                    <Stack direction="row" spacing={2} alignItems="center" display="flex" justifyContent="flex-start">
                        <StorageIcon color="info" />
                        <FormControl>
                            <InputLabel>Database</InputLabel>
                            <Select
                                value={currentDBName}
                                label="Database"
                                onChange={switchDatabase}
                                sx={{width:300}}
                            >
                                {
                                    SqlDBs.map((db: Database, index) => (
                                        <MenuItem key={index} value={db.value}>{db.name}</MenuItem>
                                    )
                                )}
                            </Select>
                        </FormControl>
                        <FormControl sx={{ minWidth: 200 }} variant="outlined">
                            <OutlinedInput
                                aria-label='search tables...'
                                title='search tables...'
                                placeholder={"Search..."}
                                onChange={updateSearchInput}
                                startAdornment={
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                }
                            />
                        </FormControl>
                        {/*
                        <ToggleButtonGroup
                            color="primary"
                            size="small"
                            value={currentDBName}
                            exclusive
                            fullWidth
                            aria-label="Databases"
                            onChange={switchDatabase}
                        >
                            {
                                SqlDBs.map((db: Database) => (
                                    <ToggleButton
                                        key={db.value}
                                        value={db.value}
                                        aria-label={`database - ${db.name}`}
                                        sx={{ fontWeight: 800}}
                                    >
                                        {db.name}
                                    </ToggleButton>
                                )
                            )}
                        </ToggleButtonGroup>
                        */}
                        
                    </Stack>
                </Grid>
                <Grid item xs={11} marginTop={5}>
                    {
                        dbObjects && dbObjects.length > 0 &&
                        <TableContainer
                            component={Paper}
                            sx={{ maxHeight: 800 }} //
                            >
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#FFFFFF'}}>
                                        <TableCell><Typography variant='body1' fontWeight={550}>TABLE</Typography></TableCell>
                                        <TableCell><Typography variant='body1' fontWeight={550}>SELECT</Typography></TableCell>
                                        <TableCell><Typography variant='body1' fontWeight={550}>INSERT</Typography></TableCell>
                                        <TableCell><Typography variant='body1' fontWeight={550}>UPDATE</Typography></TableCell>
                                        <TableCell><Typography variant='body1' fontWeight={550}>DELETE</Typography></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {dbObjects.map((object: DatabaseObject, index) =>
                                        <StyledTableRow key={index}>
                                            <TableCell width="30%">
                                                <Typography variant="body2">{object.Name}</Typography>
                                            </TableCell>
                                            {/* Option 1: permissions only in one column??
                                                 <TableCell
                                                sx={{
                                                    whiteSpace: 'normal',
                                                    wordWrap: 'break-word',
                                                    //maxWidth: 600, // optional: constrain width
                                                }}
                                            >
                                                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                                                    {
                                                        object.Permissions.map((permission: DatabasePermission, idx) => displayStatus(permission))
                                                    }
                                                </Stack>
                                            </TableCell>
                                            */}
                                            <TableCell
                                                sx={{
                                                    whiteSpace: 'normal',
                                                    wordWrap: 'break-word',
                                                    maxWidth: "25%"
                                                }}
                                            >
                                                {displaySelectPermissions(object.Permissions)}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    whiteSpace: 'normal',
                                                    wordWrap: 'break-word',
                                                    maxWidth: "25%"
                                                }}
                                            >
                                                {displayInsertPermissions(object.Permissions)}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    whiteSpace: 'normal',
                                                    wordWrap: 'break-word',
                                                    maxWidth: "25%"
                                                }}
                                            >
                                                {displayUpdatePermissions(object.Permissions)}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    whiteSpace: 'normal',
                                                    wordWrap: 'break-word',
                                                    maxWidth: "20%"
                                                }}
                                            >
                                                {displayDeletePermissions(object.Permissions)}
                                            </TableCell>
                                        </StyledTableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    }
                </Grid> 
            </Grid>
            {
                alertInfo &&
                <AlertInfoDialog isAlertInfoDialogOpen={isAlertInfoDialogOpen} alertInfo={alertInfo} closeAlertInfoDialog={closeAlertInfoDialog}></AlertInfoDialog>
            }
        </div>
    );
};

export default SqlServerStatus;